import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import EventsSlider from "../EventsSlider/EventsSlider";
import { timelineData } from "../../data/timelineData";
import "./Timeline.scss";
const RADIUS = 265;
export const degToRad = (deg) => (deg * Math.PI) / 180;
const lastPeriodIndex = timelineData.periods.length - 1;
const Timeline = () => {
    const [activePeriodIndex, setActivePeriod] = useState(lastPeriodIndex);
    const [animatedStartYear, setAnimatedStartYear] = useState(timelineData.periods[lastPeriodIndex].startYear);
    const [animatedEndYear, setAnimatedEndYear] = useState(timelineData.periods[lastPeriodIndex].endYear);
    const [currentPeriod, setCurrentPeriod] = useState(timelineData.periods[activePeriodIndex]);
    const startYearRef = useRef(null);
    const endYearRef = useRef(null);
    const eventsRef = useRef(null);
    const totalPeriods = timelineData.periods.length;
    const pointsRef = useRef([]);
    useGSAP(() => {
        const period = timelineData.periods[activePeriodIndex];
        const startObj = { value: animatedStartYear };
        const endObj = { value: animatedEndYear };
        const setStart = gsap.quickSetter(startObj, "value");
        const setEnd = gsap.quickSetter(endObj, "value");
        gsap.to(startObj, {
            value: period.startYear,
            duration: 1,
            ease: "power2.out",
            onUpdate: () => setAnimatedStartYear(Math.round(startObj.value)),
        });
        gsap.to(endObj, {
            value: period.endYear,
            duration: 1,
            ease: "power2.out",
            onUpdate: () => setAnimatedEndYear(Math.round(endObj.value)),
        });
    }, { dependencies: [activePeriodIndex] });
    const anglesRef = useRef([]);
    // Появление точек после инициализации компонента
    useGSAP(() => {
        const step = 360 / timelineData.periods.length;
        anglesRef.current = timelineData.periods.map((_, index) => index * step - 60);
        pointsRef.current.forEach((point, index) => {
            if (!point)
                return;
            const angle = anglesRef.current[index];
            gsap.set(point, {
                x: RADIUS + Math.cos(degToRad(angle)) * RADIUS,
                y: RADIUS + Math.sin(degToRad(angle)) * RADIUS,
            });
        });
    }, []);
    // Поворот точек
    useGSAP(() => {
        const step = 360 / timelineData.periods.length;
        pointsRef.current.forEach((point, index) => {
            if (!point)
                return;
            const fromAngle = anglesRef.current[index];
            const toAngle = (index - activePeriodIndex) * step - 60;
            const angleObj = { value: fromAngle };
            gsap.to(angleObj, {
                value: toAngle,
                duration: 1,
                ease: "power2.inOut",
                onUpdate: () => {
                    anglesRef.current[index] = angleObj.value;
                    gsap.set(point, {
                        x: RADIUS + Math.cos(degToRad(angleObj.value)) * RADIUS,
                        y: RADIUS + Math.sin(degToRad(angleObj.value)) * RADIUS,
                    });
                },
                onComplete: () => {
                    if (index === activePeriodIndex) {
                        // В прямом направлении активируем лейбл только после завершения поворота
                        gsap.set(pointsRef.current[index], {
                            className: "timeline__point timeline__point--active timeline__point--label",
                        });
                    }
                    else {
                        gsap.set(pointsRef.current[index], {
                            className: "timeline__point",
                        });
                    }
                },
            });
        });
    }, { dependencies: [activePeriodIndex] });
    // fade-эффект слайдера
    useGSAP(async () => {
        if (!eventsRef.current ||
            timelineData.periods[activePeriodIndex] === currentPeriod)
            return;
        gsap.killTweensOf(eventsRef.current);
        const tl = gsap.timeline();
        tl.to(eventsRef.current, {
            autoAlpha: 0,
            duration: 1,
            ease: "cubic-bezier(0,.8,0,1)",
            onComplete: () => {
                setCurrentPeriod(timelineData.periods[activePeriodIndex]);
                gsap.fromTo(eventsRef.current, { autoAlpha: 0, y: 6 }, {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.3,
                    ease: "cubic-bezier(0,.8,0,1)",
                    overwrite: true,
                });
            },
        });
    }, { dependencies: [activePeriodIndex] });
    const handleNavigation = (direction) => {
        let newIndex = activePeriodIndex;
        if (direction === "prev" && activePeriodIndex > 0) {
            newIndex = activePeriodIndex - 1;
        }
        else if (direction === "next" && activePeriodIndex < totalPeriods - 1) {
            newIndex = activePeriodIndex + 1;
        }
        if (newIndex !== activePeriodIndex) {
            setActivePeriod(newIndex);
        }
    };
    return (_jsx("section", { className: "timeline", children: _jsxs("div", { className: "timeline__margin-left", children: [_jsxs("svg", { className: "lines", viewBox: "0 0 100 100", preserveAspectRatio: "none", children: [_jsx("line", { x1: "0", y1: "0", x2: "0", y2: "100", stroke: "rgba(66, 86, 122, 0.1)", strokeWidth: "1", vectorEffect: "non-scaling-stroke" }), _jsx("line", { x1: "100", y1: "0", x2: "100", y2: "100", stroke: "rgba(66, 86, 122, 0.1)", strokeWidth: "1px", vectorEffect: "non-scaling-stroke" }), _jsx("line", { x1: "50", y1: "0", x2: "50", y2: "100", stroke: "rgba(66, 86, 122, 0.1)", strokeWidth: "1px", vectorEffect: "non-scaling-stroke" }), _jsx("line", { x1: "0", y1: "50", x2: "100", y2: "50", stroke: "rgba(66, 86, 122, 0.1)", strokeWidth: "1px", vectorEffect: "non-scaling-stroke" })] }), _jsxs("div", { className: "timeline__container", children: [_jsx("header", { className: "timeline__header", children: _jsxs("h1", { className: "timeline__title", children: ["\u0418\u0441\u0442\u043E\u0440\u0438\u0447\u0435\u0441\u043A\u0438\u0435", _jsx("br", {}), "\u0434\u0430\u0442\u044B"] }) }), _jsxs("div", { className: "timeline__content", children: [_jsxs("div", { className: "timeline__years", children: [_jsx("h2", { ref: startYearRef, className: "timeline__year timeline__year--start", children: animatedStartYear }), _jsx("h2", { ref: endYearRef, className: "timeline__year timeline__year--end", children: animatedEndYear })] }), _jsx("div", { className: "timeline__break-line" }), _jsxs("div", { className: "timeline__circle", children: [_jsx("svg", { className: "timeline__circle-svg", width: "530", height: "530", viewBox: "0 0 530 530", children: _jsx("circle", { cx: "265", cy: "265", r: "265", fill: "none", stroke: "rgba(66, 86, 122, 0.1)", strokeWidth: "1", vectorEffect: "non-scaling-stroke" }) }), timelineData.periods.map((period, index) => (_jsx("div", { ref: (el) => {
                                                el ? (pointsRef.current[index] = el) : "";
                                            }, className: `timeline__point ${index === activePeriodIndex ? "timeline__point--active" : ""}`, onClick: () => setActivePeriod(index), "data-number": period.id, "data-title": period.title }, period.id)))] }), _jsxs("div", { className: "timeline__navigation", children: [_jsxs("span", { className: "timeline__counter", children: [String(activePeriodIndex + 1).padStart(2, "0"), "/", String(totalPeriods).padStart(2, "0")] }), _jsxs("div", { className: "timeline__navigation--buttons", children: [_jsx("button", { className: `timeline__nav-button ${activePeriodIndex === 0 ? "timeline__nav-button--disabled" : ""}`, onClick: () => handleNavigation("prev"), disabled: activePeriodIndex === 0, children: _jsx("svg", { width: "9", height: "14", viewBox: "0 0 9 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M7.66418 0.707108L1.41419 6.95711L7.66418 13.2071", stroke: "#42567A", strokeWidth: "2" }) }) }, 0), _jsx("button", { className: `timeline__nav-button ${activePeriodIndex === totalPeriods - 1 ? "timeline__nav-button--disabled" : ""}`, onClick: () => handleNavigation("next"), disabled: activePeriodIndex === totalPeriods - 1, children: _jsx("svg", { width: "9", height: "14", viewBox: "0 0 9 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M0.707092 0.707108L6.95709 6.95711L0.707093 13.2071", stroke: "#42567A", strokeWidth: "2" }) }) }, 1)] })] }), _jsx("div", { className: "timeline__events", ref: eventsRef, children: _jsx(EventsSlider, { events: currentPeriod.events }) })] })] })] }) }));
};
export default Timeline;
